import { useEffect, useRef } from 'react';
import type { SubscribeSuccessState } from '../subscribe/types';

declare global {
  interface Window {
    SR?: {
      init: (config: { trackerKey: string; trackingDomain: string }) => void;
      event: { sendFormData: (formName: string, payload: Record<string, unknown>) => void };
    };
    SyneriseObjectNamespace?: string;
    onSyneriseLoad?: () => void;
  }
}

const COOKIE_NAME = '_snrs_uuid';
const TRACKER_KEY = process.env.REACT_APP_SYNERISE_TRACKER_KEY || '';
const TRACKING_DOMAIN = process.env.REACT_APP_SYNERISE_TRACKING_DOMAIN || 'https://api.skoda-auto.pl';

function hasSyneriseCookie(): boolean {
  return document.cookie.split('; ').some((c) => c.startsWith(`${COOKIE_NAME}=`));
}

/** Loads the Synerise client SDK exactly as typ.html.twig did, verbatim tracker key/domain. */
function loadSyneriseSdk(): void {
  if (window.SR) return;
  window.onSyneriseLoad = () => {
    window.SR?.init({ trackerKey: TRACKER_KEY, trackingDomain: TRACKING_DOMAIN });
  };
  const s = document.createElement('script');
  s.async = true;
  s.src = '//api.skoda-auto.pl/9hvjfax0.js';
  s.onload = window.onSyneriseLoad;
  document.body.appendChild(s);
}

/**
 * Client-side Synerise tracking for the thank-you page. If the `_snrs_uuid` cookie is still
 * absent after a short delay (SDK blocked/ad-blocker/no-JS-on-first-load), the caller should
 * fall back to the server-side `/synerise-fallback` endpoint — mirrors
 * SyneriseCookieCheck::hasSyneriseCookie() in the legacy Symfony app.
 */
export function useSyneriseTracking(state: SubscribeSuccessState | undefined, onFallbackNeeded: () => void) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!state || firedRef.current) return;
    firedRef.current = true;

    loadSyneriseSdk();

    const payload: Record<string, unknown> = {
      orderID: state.orderId,
      email: state.email,
      phone: state.phone,
      firstname: state.firstName,
      lastname: state.lastName,
      newsletterAgreement: state.agreeNewsletter,
      receive_smses: state.agreeTestDrive,
    };
    if (state.extraCheckbox1 !== undefined) payload.szukam_samochodu = state.extraCheckbox1;
    if (state.extraCheckbox2 !== undefined) payload.wlasciciel_samochodu = state.extraCheckbox2;

    const timer = window.setTimeout(() => {
      if (window.SR && state.formName) {
        window.SR.event.sendFormData(state.formName, payload);
      }
      window.setTimeout(() => {
        if (!hasSyneriseCookie()) onFallbackNeeded();
      }, 1000);
    }, 1000);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}
